import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  getDoc,
  getDocs,
  query,
  where,
  increment,
  writeBatch,
} from 'firebase/firestore';
import { appendEntry } from './ledgerService';
import { isAdminRole } from './roles';

const BANK_LINES = 'bank_lines';
const CLIENT_BAL = 'client_balance_entries';
const LEDGER = 'ledger';

const roundUsd = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const reconcileClientAvailableBalance = async (clientId) => {
  if (!clientId) return 0;
  const balanceQuery = query(collection(db, CLIENT_BAL), where('clientId', '==', clientId));
  const balanceSnap = await getDocs(balanceQuery);
  let availableBalance = 0;
  balanceSnap.forEach((entry) => {
    availableBalance += Number(entry.data()?.amountUsd || 0);
  });
  availableBalance = roundUsd(availableBalance);
  await updateDoc(doc(db, 'clients', clientId), {
    availableBalance,
    availableBalanceUpdatedAt: serverTimestamp(),
  });
  return availableBalance;
};

const reconcileClientBalanceWithoutFailingDeposit = async (clientId) => {
  try {
    return await reconcileClientAvailableBalance(clientId);
  } catch (error) {
    // The balance entry is the source of truth; do not report a committed deposit as failed.
    console.warn('Client available-balance reconciliation failed:', error);
    return null;
  }
};

// Flat USD fee for bank-transfer (direct) deposits when finance config has none saved
export const DEFAULT_TRANSFER_FEE_USD = 5;

export const getTransferFeeUsd = (financeConfig) => {
  const raw = financeConfig?.transferFeeUsd;
  const n = Number(raw);
  return raw === undefined || raw === null || raw === '' || !Number.isFinite(n)
    ? DEFAULT_TRANSFER_FEE_USD
    : Math.max(0, roundUsd(n));
};

export const calculateAutoDepositFee = ({ amountUsd, paymentMethod, financeConfig }) => {
  const amount = Math.max(0, Number(amountUsd) || 0);
  const method = String(paymentMethod || '').toLowerCase();
  if (method === 'direct_deposit') return amount > 0 ? getTransferFeeUsd(financeConfig) : 0;

  const configuredPercent = method === 'check_deposit'
    ? financeConfig?.checkProcessingPercent
    : (method === 'credit_card_deposit' ? financeConfig?.creditCardProcessingPercent : 0);
  const percent = Math.max(0, Math.min(100, Number(configuredPercent) || 0));
  return roundUsd(amount * (percent / 100));
};

export const createAutoClientDeposit = async ({
  date,
  amountUsd,
  clientId,
  clientName,
  invoiceNumber,
  paymentMethod,
  ucenaCompanyId,
  financeConfig,
  createdBy,
  contractId = null,
  milestoneId = null,
}) => {
  if (!createdBy) throw new Error('Unauthorized: missing creator');
  const userSnap = await getDoc(doc(db, 'users', createdBy));
  if (!userSnap.exists() || !isAdminRole(userSnap.data()?.role)) {
    throw new Error('Unauthorized: only Super Admin can add bank deposits');
  }

  const amount = roundUsd(amountUsd);
  const invoice = String(invoiceNumber || '').trim();
  const method = String(paymentMethod || '').trim();
  if (!date) throw new Error('Deposit date is required');
  if (!Number.isFinite(amount) || !(amount > 0)) throw new Error('Deposit amount must be greater than zero');
  if (!clientId) throw new Error('Client is required');
  if (!invoice) throw new Error('Invoice number is required');
  if (!['direct_deposit', 'check_deposit', 'credit_card_deposit'].includes(method)) {
    throw new Error('Deposit method is required');
  }

  const fee = calculateAutoDepositFee({ amountUsd: amount, paymentMethod: method, financeConfig });
  if (fee > 0 && !ucenaCompanyId) throw new Error('An active UCENA company entity is required to post the bank fee');

  let contractRef = null;
  let contractLogs = null;
  let contractLogIndex = -1;
  if (contractId || milestoneId) {
    if (!contractId || !milestoneId) throw new Error('Both contract and receivable IDs are required');
    contractRef = doc(db, 'contracts', contractId);
    const contractSnap = await getDoc(contractRef);
    if (!contractSnap.exists()) throw new Error('Contract not found');
    contractLogs = Array.isArray(contractSnap.data()?.milestoneLogs)
      ? contractSnap.data().milestoneLogs.map((log) => ({ ...log }))
      : [];
    contractLogIndex = contractLogs.findIndex((log) => log.id === milestoneId);
    if (contractLogIndex < 0) throw new Error('Receivable not found');
    if (String(contractLogs[contractLogIndex].status || '') !== 'Invoiced') {
      throw new Error('Deposits can only be posted for Invoiced receivables');
    }
    if (contractLogs[contractLogIndex].autoDepositBankLineId) {
      throw new Error('A deposit has already been posted for this invoice');
    }
  }

  const methodLabels = {
    direct_deposit: 'Direct Deposit',
    check_deposit: 'Check Deposit',
    credit_card_deposit: 'Credit Card Deposit',
  };
  const methodLabel = methodLabels[method];
  const displayClient = String(clientName || '').trim();
  const depositDescription = `${displayClient ? `${displayClient} — ` : ''}${methodLabel} received — Invoice ${invoice}`;
  const feeDescription = `${methodLabel} fee — Invoice ${invoice}`;
  const timestamp = serverTimestamp();
  const batch = writeBatch(db);

  const stableDepositKey = contractId && milestoneId
    ? `${contractId}_${milestoneId}`.replace(/[^a-zA-Z0-9_-]/g, '_')
    : '';
  const bankLineRef = stableDepositKey
    ? doc(db, BANK_LINES, `auto_deposit_${stableDepositKey}`)
    : doc(collection(db, BANK_LINES));
  const clientBalanceRef = stableDepositKey
    ? doc(db, CLIENT_BAL, `auto_deposit_${stableDepositKey}`)
    : doc(collection(db, CLIENT_BAL));
  batch.set(bankLineRef, {
    date,
    amountUsd: amount,
    currency: 'USD',
    description: depositDescription,
    reference: invoice,
    invoiceNumber: invoice,
    paymentMethod: method,
    contractId,
    milestoneId,
    state: 'client',
    clientId,
    clientBalanceEntryId: clientBalanceRef.id,
    createdAt: timestamp,
    createdBy,
    explainedAt: timestamp,
    explainedBy: createdBy,
  });
  batch.set(clientBalanceRef, {
    clientId,
    date,
    amountUsd: amount,
    bankLineId: bankLineRef.id,
    memo: depositDescription,
    invoiceNumber: invoice,
    paymentMethod: method,
    contractId,
    milestoneId,
    isReversal: false,
    createdAt: timestamp,
    createdBy,
  });
  batch.update(doc(db, 'clients', clientId), {
    availableBalance: increment(amount),
    availableBalanceUpdatedAt: timestamp,
  });

  let feeBankLineId = null;
  let ledgerEntryId = null;
  if (fee > 0) {
    const feeBankLineRef = stableDepositKey
      ? doc(db, BANK_LINES, `auto_deposit_fee_${stableDepositKey}`)
      : doc(collection(db, BANK_LINES));
    const ledgerEntryRef = stableDepositKey
      ? doc(db, LEDGER, `auto_deposit_fee_${stableDepositKey}`)
      : doc(collection(db, LEDGER));
    feeBankLineId = feeBankLineRef.id;
    ledgerEntryId = ledgerEntryRef.id;
    batch.set(feeBankLineRef, {
      date,
      amountUsd: -fee,
      currency: 'USD',
      description: feeDescription,
      reference: invoice,
      invoiceNumber: invoice,
      paymentMethod: method,
      contractId,
      milestoneId,
      state: 'entity',
      directAccountType: 'company',
      directAccountId: ucenaCompanyId,
      ledgerEntryId: ledgerEntryRef.id,
      createdAt: timestamp,
      createdBy,
      explainedAt: timestamp,
      explainedBy: createdBy,
    });
    batch.set(ledgerEntryRef, {
      postedAt: date,
      currency: 'USD',
      amountUsd: -fee,
      directAccountType: 'company',
      directAccountId: ucenaCompanyId,
      subAccount: null,
      source: 'Banking-E',
      eventType: 'Bank-E',
      memo: feeDescription,
      serviceId: null,
      serviceName: '',
      contractId,
      paymentLineId: milestoneId,
      milestoneId,
      bankLineId: feeBankLineRef.id,
      invoiceNumber: invoice,
      paymentMethod: method,
      isReversal: false,
      reversalOf: null,
      createdAt: timestamp,
      createdBy,
    });
  }

  if (contractRef && contractLogs && contractLogIndex >= 0) {
    contractLogs[contractLogIndex] = {
      ...contractLogs[contractLogIndex],
      autoDepositBankLineId: bankLineRef.id,
      autoDepositClientBalanceEntryId: clientBalanceRef.id,
      autoDepositFeeBankLineId: feeBankLineId,
      autoDepositLedgerEntryId: ledgerEntryId,
      autoDepositAmountUsd: amount,
      autoDepositFeeUsd: fee,
      autoDepositPaymentMethod: method,
      autoDepositedAt: new Date().toISOString(),
      autoDepositedBy: createdBy,
    };
    batch.update(contractRef, { milestoneLogs: contractLogs });
  }

  await batch.commit();
  const availableBalance = await reconcileClientBalanceWithoutFailingDeposit(clientId);
  return {
    bankLineId: bankLineRef.id,
    clientBalanceEntryId: clientBalanceRef.id,
    feeBankLineId,
    ledgerEntryId,
    amountUsd: amount,
    feeUsd: fee,
    availableBalance,
  };
};

export const createBankLine = async ({ date, amountUsd, description, reference, createdBy }) => {
  // Authorization: only Super Admins may create bank transactions
  if (!createdBy) {
    throw new Error('Unauthorized: missing creator');
  }
  const userSnap = await getDoc(doc(db, 'users', createdBy));
  if (!userSnap.exists() || !isAdminRole(userSnap.data()?.role)) {
    throw new Error('Unauthorized: only Super Admin can add bank transactions');
  }
  const payload = {
    date, amountUsd: Number(amountUsd), currency: 'USD', description: description || '', reference: reference || '',
    state: 'unexplained', createdAt: serverTimestamp(), createdBy
  };
  const ref = await addDoc(collection(db, BANK_LINES), payload);
  return { id: ref.id, ...payload };
};

export const explainAsClient = async ({ bankLineId, clientId, memo, explainedBy }) => {
  const bankSnap = await getDoc(doc(db, BANK_LINES, bankLineId));
  if (!bankSnap.exists()) throw new Error('Bank line not found');
  const bank = bankSnap.data();
  const amountUsd = Number(bank.amountUsd);
  const entry = {
    clientId,
    date: new Date().toISOString().slice(0,10),
    amountUsd,
    bankLineId,
    memo: memo?.text || 'Bank explained as Client',
    isReversal: false,
    createdAt: serverTimestamp(),
    createdBy: explainedBy,
  };
  const ref = doc(collection(db, CLIENT_BAL));
  const timestamp = serverTimestamp();
  const batch = writeBatch(db);
  batch.set(ref, entry);
  batch.update(doc(db, BANK_LINES, bankLineId), {
    state: 'client',
    clientId,
    clientBalanceEntryId: ref.id,
    explainedAt: timestamp,
    explainedBy,
  });
  batch.update(doc(db, 'clients', clientId), {
    availableBalance: increment(amountUsd),
    availableBalanceUpdatedAt: timestamp,
  });
  await batch.commit();
  const availableBalance = await reconcileClientBalanceWithoutFailingDeposit(clientId);
  return { clientBalanceEntryId: ref.id, availableBalance };
};

export const explainAsEntity = async ({ bankLineId, directAccountType, directAccountId, amountUsd, description, explainedBy }) => {
  const ledger = await appendEntry({
    postedAt: new Date().toISOString().slice(0,10),
    amountUsd: Number(amountUsd),
    directAccountType, directAccountId,
    source: 'Banking', eventType: 'Bank', memo: description || 'Bank explained as Entity',
    bankLineId, createdBy: explainedBy,
  });
  await updateDoc(doc(db, BANK_LINES, bankLineId), { state: 'entity', directAccountType, directAccountId, ledgerEntryId: ledger.id, explainedAt: serverTimestamp(), explainedBy });
  return ledger;
};

export const annulBankLine = async ({ bankLineId, state, linked }) => {
  // UI should ensure downstream reversals are handled before calling this
  if (state === 'client') {
    const reversalAmount = -Number(linked.amountUsd);
    const reversalRef = doc(collection(db, CLIENT_BAL));
    const timestamp = serverTimestamp();
    const batch = writeBatch(db);
    batch.set(reversalRef, {
      clientId: linked.clientId,
      date: new Date().toISOString().slice(0,10),
      amountUsd: reversalAmount,
      bankLineId,
      memo: 'Annulment reversal',
      isReversal: true,
      reversalOf: linked.clientBalanceEntryId || null,
      createdAt: timestamp,
    });
    batch.update(doc(db, 'clients', linked.clientId), {
      availableBalance: increment(reversalAmount),
      availableBalanceUpdatedAt: timestamp,
    });
    batch.update(doc(db, BANK_LINES, bankLineId), { state: 'annulled', annulledAt: timestamp });
    await batch.commit();
    await reconcileClientBalanceWithoutFailingDeposit(linked.clientId);
    return;
  } else if (state === 'entity') {
    await appendEntry({
      postedAt: new Date().toISOString().slice(0,10),
      amountUsd: -Number(linked.amountUsd),
      directAccountType: linked.directAccountType, directAccountId: linked.directAccountId,
      source: 'Banking', eventType: 'Cancelled', memo: 'Annulment reversal',
      bankLineId, isReversal: true, reversalOf: linked.ledgerEntryId,
    });
  }
  await updateDoc(doc(db, BANK_LINES, bankLineId), { state: 'annulled', annulledAt: serverTimestamp() });
};

export default { createBankLine, createAutoClientDeposit, calculateAutoDepositFee, explainAsClient, explainAsEntity, annulBankLine };
